import React, { useState, useEffect, useCallback } from 'react';
import {
  Radio,
  Wifi,
  WifiOff,
  AlertCircle,
  Trash2,
  ChevronDown,
  ChevronUp,
  Clock,
  Tag,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader,
  Circle,
  Crosshair,
  Rocket,
  Skull,
  Globe,
  Network,
  Radar,
} from 'lucide-react';
import { beaconsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../common/PageHeader';
import ConfirmModal from '../common/ConfirmModal';
import DeployBeaconModal from './DeployBeaconModal';
import { EmptyState } from '../common/Shinobi';

import { formatRelativeTime as sharedRelativeTime, formatAbsoluteDate } from '../../utils/time';

const formatRelativeTime = (isoString) => {
  if (!isoString) return 'Never';
  return sharedRelativeTime(isoString);
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  return formatAbsoluteDate(isoString);
};

const STATUS_CONFIG = {
  online: {
    label: 'Online',
    dot: 'bg-gradient-to-br from-shinobi-green to-shinobi-green-light',
    text: 'text-shinobi-green',
    badge: 'bg-gradient-to-br from-shinobi-green/15 to-shinobi-green-light/10 text-shinobi-green border border-wasabi-300',
    icon: Wifi,
  },
  offline: {
    label: 'Offline',
    dot: 'bg-gray-500',
    text: 'text-slate-500',
    badge: 'bg-gray-100 text-slate-500 border border-gray-300',
    icon: WifiOff,
  },
  error: {
    label: 'Error',
    dot: 'bg-danger-red',
    text: 'text-danger-red',
    badge: 'bg-danger-red/10 text-danger-red border border-danger-red/30',
    icon: AlertCircle,
  },
  destroying: {
    label: 'Destroying',
    dot: 'bg-shinobi-orange',
    text: 'text-shinobi-orange',
    badge: 'bg-shinobi-orange/10 text-shinobi-orange border border-shinobi-orange/30',
    icon: AlertCircle,
  },
};

const TASK_STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Circle, color: 'text-slate-500' },
  assigned: { label: 'Assigned', icon: Loader, color: 'text-neon-blue' },
  running: { label: 'Running', icon: Loader, color: 'text-shinobi-orange' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'text-neon-green' },
  failed: { label: 'Failed', icon: XCircle, color: 'text-danger-red' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'text-gray-500' },
};

const PROFILE_CONFIG = {
  pentester: {
    label: 'Pentester',
    icon: Crosshair,
    badge: 'bg-shinobi-purple/10 text-shinobi-purple border border-shinobi-purple/30',
  },
  dast: {
    label: 'DAST',
    icon: Globe,
    badge: 'bg-cyan-100 text-cyan-700 border border-cyan-300',
  },
  internal_scanner: {
    label: 'Internal Scanner',
    icon: Network,
    badge: 'bg-amber-100 text-amber-700 border border-amber-300',
  },
  network_monitor: {
    label: 'Network Monitor',
    icon: Radar,
    badge: 'bg-blue-100 text-blue-700 border border-blue-300',
  },
};

// Binary kind. pentest carries command-exec capability; monitor is a
// physically-stripped passive scanning binary. Shown as a tiny inline badge so
// operators can see at a glance which binary each beacon is running.
const KIND_CONFIG = {
  pentest: {
    label: 'pentest',
    badge: 'bg-shinobi-purple/5 text-shinobi-purple/80 border border-shinobi-purple/20',
  },
  monitor: {
    label: 'monitor',
    badge: 'bg-wasabi-100 text-wasabi-700 border border-wasabi-300',
  },
};

const MetricBar = ({ label, value, color = 'bg-shinobi-purple' }) => (
  <div className="flex items-center space-x-2">
    <span className="text-xs text-gray-500 w-12 flex-shrink-0">{label}</span>
    <div className="flex-1 bg-cyber-300 rounded-full h-1.5 overflow-hidden">
      <div
        className={`h-full ${color} rounded-full transition-all`}
        style={{ width: `${Math.min(value || 0, 100)}%` }}
      />
    </div>
    <span className="text-xs text-slate-500 w-10 text-right font-mono">
      {value != null ? `${value.toFixed(0)}%` : '—'}
    </span>
  </div>
);

const TaskRow = ({ task }) => {
  const statusCfg = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;

  return (
    <tr className="border-t border-slate-200 hover:bg-cyber-200/30 transition-colors">
      <td className="px-4 py-3">
        <span className="text-xs font-mono text-neon-blue bg-neon-blue/10 px-2 py-0.5 rounded">
          {task.scanner || '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-slate-700 font-mono">{task.target || '—'}</span>
      </td>
      <td className="px-4 py-3">
        <div className={`flex items-center space-x-1.5 ${statusCfg.color}`}>
          <StatusIcon className="w-3.5 h-3.5" />
          <span className="text-xs capitalize">{statusCfg.label}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-slate-500">
          {task.results_count != null ? task.results_count.toLocaleString() : '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-gray-500">{formatDateTime(task.started_at)}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-gray-500">{formatDateTime(task.completed_at)}</span>
      </td>
    </tr>
  );
};

// Inline vault label editor for monitor-kind beacons. Optimistic local update;
// the next list refresh reconciles. Uses the existing beacons PATCH endpoint.
const VaultField = ({ beacon }) => {
  const [editing, setEditing] = useState(false);
  const [current, setCurrent] = useState(beacon.vault || '');
  const [value, setValue] = useState(beacon.vault || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await beaconsAPI.update(beacon.id, { vault: value.trim() });
      setCurrent(value.trim());
      setEditing(false);
    } catch (err) {
      console.error('Failed to update vault:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">Vault</p>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. Hillsboro (US)"
            className="terminal-input px-3 py-1.5 rounded text-sm flex-1 min-w-[160px]"
            autoFocus
          />
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1.5 rounded text-xs bg-wasabi-100 text-wasabi-700 border border-wasabi-300 hover:bg-shinobi-green/25 transition-colors disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={() => { setValue(current); setEditing(false); }}
            className="px-3 py-1.5 rounded text-xs border border-slate-200 text-slate-500 hover:text-slate-800 transition-colors"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${current ? 'bg-wasabi-100 text-wasabi-700 border border-shinobi-green/25' : 'bg-cyber-200/50 text-gray-500 border border-slate-200'}`}>
            {current || 'Unassigned'}
          </span>
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-gray-500 hover:text-shinobi-green transition-colors"
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

const BeaconDetailPanel = ({ beacon, onDeleteClick, onDestroyClick }) => {
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState(null);

  useEffect(() => {
    const fetchTasks = async () => {
      setTasksLoading(true);
      setTasksError(null);
      try {
        const response = await beaconsAPI.getTaskHistory(beacon.id);
        setTasks(response.data);
      } catch (err) {
        console.error('Failed to fetch task history:', err);
        setTasksError('Failed to load task history.');
      } finally {
        setTasksLoading(false);
      }
    };
    fetchTasks();
  }, [beacon.id]);

  return (
    <div className="border-t border-slate-200 bg-cyber-50/50 px-6 py-5 space-y-5">
      {/* Metadata grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-xs text-gray-500 mb-1">Heartbeat Interval</p>
          <p className="text-sm text-slate-700 font-mono">{beacon.heartbeat_interval}s</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Poll Interval</p>
          <p className="text-sm text-slate-700 font-mono">{beacon.poll_interval}s</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Registered</p>
          <p className="text-sm text-slate-700">{formatDateTime(beacon.created_at)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Last Updated</p>
          <p className="text-sm text-slate-700">{formatDateTime(beacon.updated_at)}</p>
        </div>
      </div>

      {/* Vault (monitor beacons only) */}
      {beacon.beacon_kind === 'monitor' && <VaultField beacon={beacon} />}

      {/* External tools */}
      {beacon.external_tools && beacon.external_tools.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">External Tools</p>
          <div className="flex flex-wrap gap-2">
            {beacon.external_tools.map((tool) => (
              <span
                key={tool}
                className="px-2 py-0.5 rounded text-xs font-mono bg-shinobi-orange/10 text-shinobi-orange border border-shinobi-orange/20"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Task history */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center space-x-2">
          <Clock className="w-4 h-4 text-shinobi-purple" />
          <span>Task History</span>
        </h4>

        {tasksLoading ? (
          <div className="flex items-center space-x-2 text-gray-500 text-sm py-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-shinobi-purple" />
            <span>Loading task history...</span>
          </div>
        ) : tasksError ? (
          <p className="text-danger-red text-sm py-4">{tasksError}</p>
        ) : tasks.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No tasks recorded for this beacon.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-cyber-200">
                  <th className="px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wide">Scanner</th>
                  <th className="px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wide">Target</th>
                  <th className="px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wide">Status</th>
                  <th className="px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wide">Results</th>
                  <th className="px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wide">Started</th>
                  <th className="px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wide">Completed</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <TaskRow key={task.id} task={task} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col items-end gap-2 pt-2">
        <div className="flex items-center space-x-3">
          {beacon.os === 'linux' && (
            <button
              onClick={() => onDestroyClick(beacon)}
              disabled={beacon.status === 'offline' || beacon.status === 'destroying'}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-danger-red/10 border border-danger-red/60 text-danger-red hover:bg-danger-red/20 transition-all text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Skull className="w-4 h-4" />
              <span>Destroy Beacon</span>
            </button>
          )}
          <button
            onClick={() => onDeleteClick(beacon)}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-slate-200 text-slate-500 hover:border-danger-red/40 hover:text-danger-red transition-all text-sm"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete Record</span>
          </button>
        </div>
        {beacon.os === 'linux' && (
          <p className="text-xs text-gray-600">Destroy remotely uninstalls the beacon agent</p>
        )}
      </div>
    </div>
  );
};

const BeaconCard = ({ beacon, isExpanded, onToggle, onDeleteClick, onDestroyClick }) => {
  const statusCfg = STATUS_CONFIG[beacon.status] || STATUS_CONFIG.offline;
  const metrics = beacon.system_metrics || {};

  return (
    <div className="cyber-card rounded-lg overflow-hidden">
      {/* Card header — clickable to expand */}
      <div
        className="p-5 cursor-pointer hover:bg-cyber-200/30 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between">
          {/* Left: status + hostname */}
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Animated status dot */}
            <div className="relative flex-shrink-0 w-2.5 h-2.5">
              <span
                className={`absolute inset-0 w-2.5 h-2.5 rounded-full ${statusCfg.dot}`}
              />
              {(beacon.status === 'online' || beacon.status === 'destroying') && (
                <span
                  className={`absolute inset-0 w-2.5 h-2.5 rounded-full ${statusCfg.dot} animate-ping opacity-60`}
                />
              )}
            </div>

            <div className="min-w-0">
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <h3 className="text-base font-bold text-slate-900 truncate">
                  {beacon.label || beacon.hostname}
                </h3>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusCfg.badge}`}>
                  {statusCfg.label}
                </span>
                {beacon.network_location && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-shinobi-purple/10 text-shinobi-purple border border-shinobi-purple/20">
                    {beacon.network_location}
                  </span>
                )}
                {beacon.profile && PROFILE_CONFIG[beacon.profile] && (() => {
                  const pCfg = PROFILE_CONFIG[beacon.profile];
                  const ProfileIcon = pCfg.icon;
                  return (
                    <span className={`flex items-center space-x-1 px-2 py-0.5 rounded text-xs font-medium ${pCfg.badge}`}>
                      <ProfileIcon className="w-3 h-3" />
                      <span>{pCfg.label}</span>
                    </span>
                  );
                })()}
                {beacon.beacon_kind && KIND_CONFIG[beacon.beacon_kind] && (
                  <span
                    title={beacon.beacon_kind === 'monitor'
                      ? 'Monitor binary: no command-exec code, passive scanning only'
                      : 'Pentest binary: full offensive toolkit'}
                    className={`px-2 py-0.5 rounded text-xs font-mono ${KIND_CONFIG[beacon.beacon_kind].badge}`}
                  >
                    {KIND_CONFIG[beacon.beacon_kind].label}
                  </span>
                )}
              </div>
              {beacon.active_tasks && beacon.active_tasks.length > 0 && (
                <div className="mt-1.5 flex items-center space-x-1.5 text-xs">
                  <Loader className="w-3 h-3 animate-spin text-shinobi-orange flex-shrink-0" />
                  {beacon.active_tasks.length === 1 ? (
                    <span className="text-shinobi-orange truncate">
                      Running:{' '}
                      <span className="font-mono">{beacon.active_tasks[0].scanner}</span>
                      {' → '}
                      <span className="font-mono text-slate-700">{beacon.active_tasks[0].target}</span>
                    </span>
                  ) : (
                    <span className="text-shinobi-orange truncate">
                      Running:{' '}
                      <span className="font-semibold">{beacon.active_tasks.length}</span> tasks (latest:{' '}
                      <span className="font-mono">{beacon.active_tasks[0].scanner}</span>
                      {' → '}
                      <span className="font-mono text-slate-700">{beacon.active_tasks[0].target}</span>)
                    </span>
                  )}
                </div>
              )}
              {(!beacon.active_tasks || beacon.active_tasks.length === 0) && beacon.recent_completed_task && (
                <div className="mt-1.5 flex items-center space-x-1.5 text-xs">
                  <CheckCircle className="w-3 h-3 text-shinobi-green flex-shrink-0" />
                  <span className="text-shinobi-green truncate">
                    Just ran:{' '}
                    <span className="font-mono">{beacon.recent_completed_task.scanner}</span>
                    {' → '}
                    <span className="font-mono text-slate-700">{beacon.recent_completed_task.target}</span>
                  </span>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-0.5 font-mono">
                {beacon.hostname} &nbsp;•&nbsp; {beacon.os} / {beacon.arch} &nbsp;•&nbsp; v{beacon.version}
              </p>
            </div>
          </div>

          {/* Right: heartbeat + expand toggle */}
          <div className="flex items-center space-x-4 flex-shrink-0 ml-4">
            <div className="text-right hidden sm:block">
              <p className="text-xs text-gray-500">Last seen</p>
              <p className="text-xs text-slate-700">{formatRelativeTime(beacon.last_heartbeat)}</p>
            </div>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-slate-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-500" />
            )}
          </div>
        </div>

        {/* Capabilities */}
        {beacon.capabilities && beacon.capabilities.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {beacon.capabilities.map((cap) => (
              <span
                key={cap}
                className="px-2 py-0.5 rounded text-xs font-mono bg-neon-blue/10 text-neon-blue border border-neon-blue/20"
              >
                {cap}
              </span>
            ))}
          </div>
        )}

        {/* Tags (pool:shared is implicit in Shinobi: every beacon serves every pentester) */}
        {beacon.tags && beacon.tags.filter(t => t !== 'pool:shared').length > 0 && (
          <div className="mt-2 flex items-center space-x-1.5 flex-wrap gap-y-1">
            <Tag className="w-3 h-3 text-gray-600" />
            {beacon.tags.filter(t => t !== 'pool:shared').map((tag) => (
              <span key={tag} className="text-xs text-gray-500">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* System metrics — hide when offline */}
        {beacon.status !== 'offline' && (metrics.cpu_percent != null || metrics.memory_percent != null || metrics.disk_percent != null) && (
          <div className="mt-4 space-y-1.5">
            {metrics.cpu_percent != null && (
              <MetricBar label="CPU" value={metrics.cpu_percent} color="bg-shinobi-purple" />
            )}
            {metrics.memory_percent != null && (
              <MetricBar label="MEM" value={metrics.memory_percent} color="bg-shinobi-pink" />
            )}
            {metrics.disk_percent != null && (
              <MetricBar label="DISK" value={metrics.disk_percent} color="bg-shinobi-orange" />
            )}
          </div>
        )}
      </div>

      {/* Expanded detail panel */}
      {isExpanded && (
        <BeaconDetailPanel beacon={beacon} onDeleteClick={onDeleteClick} onDestroyClick={onDestroyClick} />
      )}
    </div>
  );
};

const BeaconManagement = () => {
  const { user, isBlueTeam } = useAuth();
  const [beacons, setBeacons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, beacon: null });
  const [confirmDestroy, setConfirmDestroy] = useState({ isOpen: false, beacon: null });
  const [profileFilter, setProfileFilter] = useState('all');
  const [deployModalOpen, setDeployModalOpen] = useState(false);

  const fetchBeacons = useCallback(async () => {
    try {
      const response = await beaconsAPI.list();
      setBeacons(response.data.beacons || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch beacons:', err);
      setError('Failed to load beacons. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBeacons();
  }, [fetchBeacons]);

  useEffect(() => {
    const interval = setInterval(fetchBeacons, 5000);
    return () => clearInterval(interval);
  }, [fetchBeacons]);

  const handleToggleExpand = (beaconId) => {
    setExpandedId((prev) => (prev === beaconId ? null : beaconId));
  };

  const handleDeleteClick = (beacon) => {
    setConfirmDelete({ isOpen: true, beacon });
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete.beacon) return;
    try {
      await beaconsAPI.delete(confirmDelete.beacon.id);
      setBeacons((prev) => prev.filter((b) => b.id !== confirmDelete.beacon.id));
      if (expandedId === confirmDelete.beacon.id) setExpandedId(null);
    } catch (err) {
      console.error('Failed to delete beacon:', err);
    }
  };

  const handleDestroyConfirm = async () => {
    if (!confirmDestroy.beacon) return;
    try {
      await beaconsAPI.destroy(confirmDestroy.beacon.id);
      setBeacons((prev) => prev.filter((b) => b.id !== confirmDestroy.beacon.id));
      if (expandedId === confirmDestroy.beacon.id) setExpandedId(null);
    } catch (err) {
      console.error('Failed to initiate beacon destroy:', err);
    }
  };

  const onlineCount = beacons.filter((b) => b.status === 'online').length;

  const filteredBeacons = profileFilter === 'all'
    ? beacons
    : beacons.filter((b) => b.profile === profileFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shinobi-purple mx-auto" />
          <p className="mt-4 text-slate-500 text-sm">Loading beacons...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Beacon Management"
        subtitle={
          beacons.length > 0
            ? `${beacons.length} registered • ${onlineCount} online`
            : 'No beacons registered'
        }
        icon={Radio}
        lastRefresh={lastRefresh?.toLocaleTimeString()}
        actions={
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setDeployModalOpen(true)}
              className="btn-cyber px-4 py-2 rounded flex items-center space-x-2"
            >
              <Rocket className="w-4 h-4" />
              <span>Deploy Beacon</span>
            </button>
            <button
              onClick={fetchBeacons}
              className="flex items-center space-x-2 px-4 py-2 rounded border border-slate-200 text-slate-700 hover:border-gray-500 hover:text-slate-900 transition-colors text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        }
      />

      <div className="p-8">
        {error && (
          <div className="mb-6 cyber-card rounded-lg p-4 border border-danger-red/40 bg-danger-red/5 flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-danger-red flex-shrink-0" />
            <p className="text-danger-red text-sm">{error}</p>
          </div>
        )}

        {beacons.length === 0 ? (
          <div className="cyber-card rounded-xl">
            <EmptyState
              icon={Radio}
              title="No beacons registered"
              description="Deploy a Wasabi Shinobi beacon agent to start collecting reconnaissance data from internal or external network locations."
              actionLabel="Deploy a Beacon"
              actionIcon={Rocket}
              onAction={() => setDeployModalOpen(true)}
              accent="green"
            />
          </div>
        ) : (
          <>
            {/* Profile filter buttons */}
            <div className="flex items-center space-x-2 mb-5">
              {(isBlueTeam
                ? [
                    { value: 'all', label: 'All', icon: Radio },
                    { value: 'internal_scanner', label: 'Internal Scanner', icon: Network },
                    { value: 'network_monitor', label: 'Network Monitor', icon: Radar },
                  ]
                : [
                    { value: 'all', label: 'All', icon: Radio },
                    { value: 'pentester', label: 'Pentester', icon: Crosshair },
                    { value: 'dast', label: 'DAST', icon: Globe },
                    { value: 'internal_scanner', label: 'Internal Scanner', icon: Network },
                  ]
              ).map(({ value, label, icon: FilterIcon }) => (
                <button
                  key={value}
                  onClick={() => setProfileFilter(value)}
                  className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm border transition-all ${
                    profileFilter === value
                      ? 'bg-shinobi-purple/20 text-shinobi-purple border-shinobi-purple/50'
                      : 'text-slate-500 border-slate-200 hover:border-gray-500 hover:text-slate-800'
                  }`}
                >
                  <FilterIcon className="w-4 h-4" />
                  <span>{label}</span>
                  <span className="text-xs text-gray-500">
                    ({value === 'all' ? beacons.length : beacons.filter((b) => b.profile === value).length})
                  </span>
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {filteredBeacons.map((beacon) => (
                <BeaconCard
                  key={beacon.id}
                  beacon={beacon}
                  isExpanded={expandedId === beacon.id}
                  onToggle={() => handleToggleExpand(beacon.id)}
                  onDeleteClick={handleDeleteClick}
                  onDestroyClick={(b) => setConfirmDestroy({ isOpen: true, beacon: b })}
                />
              ))}
              {filteredBeacons.length === 0 && (
                <div className="cyber-card rounded-lg p-12 text-center">
                  <p className="text-gray-500 text-sm">No beacons match the selected filter.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, beacon: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Beacon"
        message={
          confirmDelete.beacon
            ? `Are you sure you want to delete beacon "${confirmDelete.beacon.hostname}"? This action cannot be undone.`
            : 'Are you sure you want to delete this beacon?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmModal
        isOpen={confirmDestroy.isOpen}
        onClose={() => setConfirmDestroy({ isOpen: false, beacon: null })}
        onConfirm={handleDestroyConfirm}
        title="Destroy Beacon"
        message={
          confirmDestroy.beacon
            ? `This will permanently uninstall the beacon agent from "${confirmDestroy.beacon.hostname}". The binary, configuration, and systemd service will be removed from the remote machine. This action cannot be undone.`
            : ''
        }
        confirmText="Destroy"
        cancelText="Cancel"
        variant="danger"
      />

      <DeployBeaconModal
        isOpen={deployModalOpen}
        onClose={() => setDeployModalOpen(false)}
      />
    </div>
  );
};

const BeaconManagementGated = (props) => (
  
    <BeaconManagement {...props} />
  
);

export default BeaconManagementGated;
